import { createSlice } from '@reduxjs/toolkit';

import { registerCurrentUser, updateUserDetails } from 'api/userSupportAPI';
import { handleError } from 'services/Error';
import {
    getUpdatedReferralConversationCounts,
    mapConversation,
    mapMessage,
} from 'services/userSupportHelpers';

export const userSupportSlice = createSlice({
    name: 'userSupport',
    initialState: {
        socketConnected: false,
        connectionLoading: true,
        userProfile: undefined,
        referralConversations: {},
        currentReferral: undefined,
        currentConversation: undefined,
        conversationMessages: {},
        userParticipation: undefined,
        conversationParticipation: undefined,
        unreadConversationCount: undefined,
        referralConversationCounts: undefined,
        canLogWebsocketPerformance: undefined,
    },
    reducers: {
        setSocketConnected(state, action) {
            const { connected } = action.payload;
            return {
                ...state,
                socketConnected: connected,
            };
        },
        setConnectionLoading(state, action) {
            const { loading } = action.payload;
            return {
                ...state,
                connectionLoading: loading,
            };
        },
        updateUserProfile(state, action) {
            const { upn, firstName, lastName, lastActive } = action.payload;
            return {
                ...state,
                userProfile: {
                    upn,
                    firstName,
                    lastName,
                    lastActive,
                },
            };
        },
        updateCurrentReferral(state, action) {
            const { referralId } = action.payload ?? {};
            return {
                ...state,
                currentReferral: referralId ?? null,
            };
        },
        updateReferralConversations(state, action) {
            const { conversations } = action.payload;
            const { currentReferral } = state;
            if (!conversations) {
                return {
                    ...state,
                    referralConversations: undefined,
                };
            }
            const newReferral = Object.values(conversations)[0]?.referralId;
            const referralChange = newReferral !== currentReferral;
            return {
                ...state,
                currentReferral: newReferral,
                referralConversations: {
                    ...(referralChange ? {} : state.referralConversations),
                    ...conversations,
                },
            };
        },
        updateCurrentConversation(state, action) {
            const { conversationId } = action.payload ?? {};
            return {
                ...state,
                currentConversation: conversationId ?? null,
            };
        },
        updateConversationMessages(state, action) {
            const { conversationId, messages } = action.payload;
            return {
                ...state,
                currentConversation: conversationId,
                conversationMessages: {
                    ...state.conversationMessages,
                    [conversationId]: {
                        messages: {
                            ...(state.conversationMessages[conversationId]?.messages ?? {}),
                            ...messages,
                        },
                    },
                },
            };
        },
        updateUserParticipation(state, action) {
            const { userParticipation, conversationParticipation } = state;
            const userUpdates = action.payload;

            // Temporary to ease transition to new format
            let newUserParticipation;
            if (userUpdates.length > 0 && !!userUpdates[0].conversation_id) {
                newUserParticipation = Object.fromEntries(
                    userUpdates.map((update) => [update.conversation_id, update.last_message_seen]),
                );
            } else {
                newUserParticipation = userUpdates;
            }
            // Temporary end

            const updatedParticipation = {
                ...userParticipation,
                ...newUserParticipation,
            };

            const updatedConversationCounts = getUpdatedReferralConversationCounts(
                updatedParticipation,
                conversationParticipation,
            );

            return {
                ...state,
                userParticipation: updatedParticipation,
                referralConversationCounts: updatedConversationCounts,
                unreadConversationCount: Object.values(updatedConversationCounts ?? {}).reduce(
                    (prev, curr) => prev + curr.unread,
                    0,
                ),
            };
        },
        updateConversationParticipation(state, action) {
            const { userParticipation, conversationParticipation } = state;
            const conversationUpdates = action.payload;

            const updatedParticipation = {
                ...conversationParticipation,
                ...Object.fromEntries(
                    conversationUpdates.map((conversation) => [
                        conversation.conversation_id,
                        conversation,
                    ]),
                ),
            };

            const updatedConversationCounts = getUpdatedReferralConversationCounts(
                userParticipation,
                updatedParticipation,
            );

            return {
                ...state,
                conversationParticipation: updatedParticipation,
                referralConversationCounts: updatedConversationCounts,
                unreadConversationCount: Object.values(updatedConversationCounts ?? {}).reduce(
                    (prev, curr) => prev + curr.unread,
                    0,
                ),
            };
        },
        setCanLogWebsocketPerformance(state, action) {
            return { ...state, canLogWebsocketPerformance: action.payload };
        },
    },
});

export const {
    setSocketConnected,
    setConnectionLoading,
    updateUserProfile,
    updateCurrentReferral,
    updateReferralConversations,
    updateCurrentConversation,
    updateConversationMessages,
    setCanLogWebsocketPerformance,
    updateUserParticipation,
    updateConversationParticipation,
} = userSupportSlice.actions;

export const registerUser =
    ({ details }) =>
    async (dispatch) => {
        try {
            const userProfile = await registerCurrentUser({ details });
            dispatch(
                updateUserProfile({
                    upn: userProfile.upn,
                    firstName: userProfile.first_name,
                    lastName: userProfile.last_name,
                    lastActive: userProfile.last_active,
                }),
            );
        } catch (err) {
            handleError('An error occurred retrieving user details.', err);
        }
    };

export const updateUser =
    ({ details }) =>
    async (dispatch) => {
        try {
            const userProfile = await updateUserDetails({ details });
            dispatch(
                updateUserProfile({
                    upn: userProfile.upn,
                    firstName: userProfile.first_name,
                    lastName: userProfile.last_name,
                    lastActive: userProfile.last_active,
                }),
            );
        } catch (err) {
            handleError('An error occurred when updating user details.', err);
        }
    };

export const updateConversations = (conversations) => (dispatch) => {
    const mappedConversations = conversations.map(mapConversation);
    const conversationsList = mappedConversations.reduce(
        (prev, curr) => ({ ...prev, [curr.conversationId]: curr }),
        {},
    );
    dispatch(
        updateReferralConversations({
            conversations: conversationsList,
        }),
    );
};

export const updateMessages = (payload) => (dispatch) => {
    const mappedMessages = payload.messages.map(mapMessage);
    const conversationId = payload.conversation_id;
    const messagesList = mappedMessages.reduce(
        (prev, curr) => ({ ...prev, [curr.messageId]: curr }),
        {},
    );
    if (conversationId) {
        dispatch(updateConversationMessages({ conversationId, messages: messagesList }));
    }
};
